import React, { useState } from 'react';
import { auth, getHttpFunctionUrl, postJsonWithAuth } from '../firebase';
import ModalReautenticacao from './ModalReautenticacao';
import { calcularPrecoFilial } from '../precoFilial';
import useFaturaNovaGuia, { fecharGuiaFatura, reservarGuiaFatura } from './useFaturaNovaGuia';

function AbaMinhasFiliais({
  usuarioAtual = {},
  minhasFiliais = [],
  configGerais = {},
  onFilialPendenteAdicionada,
  onFilialPendenteRemovida,
  onFiliaisAtivadas,
}) {
  const { abrirFaturaNovaGuia, modalFatura } = useFaturaNovaGuia();
  const [codigoVinculo, setCodigoVinculo] = useState('');
  const [acaoEmProcessamento, setAcaoEmProcessamento] = useState(null);
  const [acaoPendenteReautenticacao, setAcaoPendenteReautenticacao] = useState(null);

  if (usuarioAtual?.id_usuario_pai) {
    return (
      <div className="routine-panel">
        <div className="routine-callout routine-callout--danger">
          <h3 className="routine-callout__title">Acesso restrito</h3>
          <p className="routine-callout__text">
            Sua conta está configurada como uma <strong>filial</strong>. Apenas contas <strong>matriz</strong> podem
            visualizar e gerenciar a rede de lojas.
          </p>
        </div>
      </div>
    );
  }

  const listaSegura = minhasFiliais || [];
  const filiaisAtivas = listaSegura.filter((filial) => filial.status_vinculo !== 'pendente_pagamento');
  const filiaisPendentesLegadas = listaSegura.filter((filial) => filial.status_vinculo === 'pendente_pagamento');
  const filiaisPendentesDaFila = Array.isArray(usuarioAtual?.filiais_pendentes_vinculo)
    ? usuarioAtual.filiais_pendentes_vinculo.map((filial) => ({
      ...filial,
      status_vinculo: 'pendente_pagamento',
    }))
    : [];
  const filiaisPendentes = [...filiaisPendentesDaFila, ...filiaisPendentesLegadas]
    .filter((filial, index, lista) => lista.findIndex((item) => item.id === filial.id) === index);
  const precoUnitario = calcularPrecoFilial(configGerais, usuarioAtual.plano);
  const totalLoteMensal = filiaisPendentes.length * precoUnitario;

  const iniciarVinculoPendente = async () => {
    const codigoLimpo = codigoVinculo.trim();
    if (!codigoLimpo) return alert('Digite o código da filial.');
    if (codigoLimpo === usuarioAtual?.id) return alert('Você não pode vincular sua própria conta.');

    setAcaoEmProcessamento('vincular');
    try {
      const resposta = await postJsonWithAuth(
        getHttpFunctionUrl('vincularFilialPendente'),
        { codigoFilial: codigoLimpo },
      );
      const data = await resposta.json();

      if (!resposta.ok) {
        throw new Error(data.erro || 'Erro ao vincular filial.');
      }

      setCodigoVinculo('');
      onFilialPendenteAdicionada?.(data.filial);
    } catch (error) {
      console.error(error);
      alert(error.message || 'Erro ao vincular filial.');
    } finally {
      setAcaoEmProcessamento(null);
    }
  };

  const cancelarPendente = async (filial) => {
    if (acaoEmProcessamento) return;

    setAcaoEmProcessamento('cancelar_pendente');
    try {
      const resposta = await postJsonWithAuth(
        getHttpFunctionUrl('cancelarFilialPendente'),
        { idFilial: filial.id },
      );
      const data = await resposta.json();

      if (!resposta.ok) {
        throw new Error(data.erro || 'Erro ao cancelar filial pendente.');
      }

      onFilialPendenteRemovida?.(filial.id);
    } catch (error) {
      console.error(error);
      alert(error.message || 'Erro ao cancelar a fila da filial.');
    } finally {
      setAcaoEmProcessamento(null);
    }
  };

  const agendarDesvinculo = async (filial, identidadeConfirmada = false) => {
    if (!identidadeConfirmada &&
      !window.confirm(
        `Tem certeza que deseja remover a filial ${filial.nome}? O valor será descontado da sua próxima fatura, mas a loja continuará ativa até o fim do ciclo atual.`,
      )) {
      return;
    }

    if (!identidadeConfirmada) {
      setAcaoPendenteReautenticacao({ tipo: 'desvincular', filial });
      return;
    }

    setAcaoEmProcessamento('desvincular');
    try {
      const idGarantido = auth.currentUser?.uid;
      const resposta = await postJsonWithAuth(
        getHttpFunctionUrl('agendarRemocaoFilial'),
        { uidMatriz: idGarantido, idFilial: filial.id },
      );
      const data = await resposta.json();

      if (!resposta.ok) {
        throw new Error(data.erro || 'Erro ao comunicar com o servidor financeiro.');
      }

      const dataFormatada = new Date(`${data.data_fim_ciclo}T12:00:00`).toLocaleDateString('pt-BR');
      alert(`Remoção agendada com sucesso! A filial continuará ativa até ${dataFormatada}.`);
    } catch (error) {
      console.error('Erro ao agendar remoção:', error);
      alert(error.message || 'Erro ao remover filial. Tente novamente.');
    } finally {
      setAcaoEmProcessamento(null);
    }
  };

  const gerarPagamentoFiliaisAsaas = async () => {
    if (acaoEmProcessamento) return;

    if (usuarioAtual?.link_fatura_pendente && usuarioAtual?.plano_pendente_pagamento === 'filial') {
      alert('Você já possui um pagamento de filiais pendente. Vamos abrir o link para você.');
      abrirFaturaNovaGuia(usuarioAtual.link_fatura_pendente);
      return;
    }

    setAcaoEmProcessamento('gerar_cobranca');
    let guiaFatura = null;

    try {
      const idGarantido = auth.currentUser?.uid;
      if (!idGarantido) {
        alert('Sessão inválida. Por favor, faça login novamente.');
        setAcaoEmProcessamento(null);
        return;
      }

      if (filiaisPendentes.length === 0) {
        setAcaoEmProcessamento(null);
        return;
      }

      guiaFatura = reservarGuiaFatura();
      const resposta = await postJsonWithAuth(
        getHttpFunctionUrl('modificarAssinaturaAsaas'),
        {
          uidCliente: idGarantido,
          tipoAlteracao: 'filial',
          qtdFiliaisAdicionais: filiaisPendentes.length,
        },
      );

      const data = await resposta.json();
      if (!resposta.ok) throw new Error(data.erro || 'Erro ao gerar fatura.');

      if (data.sucesso && data.linkPagamento) {
        abrirFaturaNovaGuia(data.linkPagamento, guiaFatura);
        guiaFatura = null;
      } else if (data.sucesso && data.ativacao_imediata) {
        onFiliaisAtivadas?.(data.filiais || []);
        alert(data.mensagem || 'Filiais ativadas. O valor adicional sera incluido na proxima renovacao.');
      } else {
        alert('Não foi possível gerar o link de pagamento.');
      }
    } catch (error) {
      alert(error.message || 'Erro ao abrir o pagamento.');
    } finally {
      fecharGuiaFatura(guiaFatura);
      setAcaoEmProcessamento(null);
    }
  };

  return (
    <div className="routine-panel">
      {modalFatura}
      <ModalReautenticacao
        aberto={Boolean(acaoPendenteReautenticacao)}
        titulo="Confirmar ação nas filiais"
        descricao="Confirme sua senha atual antes de concluir esta ação na sua rede de lojas."
        textoBotao="Confirmar e continuar"
        onFechar={() => setAcaoPendenteReautenticacao(null)}
        onConfirmado={async () => {
          const acaoConfirmada = acaoPendenteReautenticacao;
          setAcaoPendenteReautenticacao(null);

          if (acaoConfirmada?.tipo === 'desvincular') {
            await agendarDesvinculo(acaoConfirmada.filial, true);
          }
        }}
      />

      <div className="routine-surface" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="ID da filial (pegue no app da filial)..."
            value={codigoVinculo}
            onChange={(e) => setCodigoVinculo(e.target.value)}
            className="routine-input"
            style={{ flex: 1, minWidth: '280px' }}
          />
          <button
            type="button"
            onClick={iniciarVinculoPendente}
            disabled={Boolean(acaoEmProcessamento)}
            className="routine-btn routine-btn--primary"
          >
            {acaoEmProcessamento === 'vincular' ? 'Vinculando...' : 'Adicionar à fila'}
          </button>
        </div>
      </div>

      {filiaisPendentes.length > 0 && (
        <div className="routine-callout routine-callout--warm" style={{ marginBottom: '28px' }}>
          <div className="routine-toolbar" style={{ marginBottom: 0 }}>
            <div className="routine-title-group">
              <h4 className="routine-section-title">Lojas aguardando ativação ({filiaisPendentes.length})</h4>
              <p className="routine-section-subtitle">
                Revise o lote abaixo antes de ativar as filiais.
              </p>
            </div>
          </div>

          <div className="routine-pending-grid">
            {filiaisPendentes.map((filial) => (
              <div key={filial.id} className="routine-pending-item">
                <span style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>
                  {filial.nome || 'Carregando...'}
                </span>
                <button
                  type="button"
                  onClick={() => cancelarPendente(filial)}
                  disabled={Boolean(acaoEmProcessamento)}
                  className="routine-btn routine-btn--danger"
                >
                  {acaoEmProcessamento === 'cancelar_pendente' ? 'Removendo...' : 'Remover'}
                </button>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: '20px',
              paddingTop: '20px',
              borderTop: '1px dashed rgba(217, 119, 6, 0.32)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '14px',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <span className="routine-inline-card__label">Custo mensal adicional</span>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#b45309' }}>
                R$ {totalLoteMensal.toFixed(2)}
              </div>
            </div>

            <button
              type="button"
              className="routine-btn routine-btn--warning"
              onClick={gerarPagamentoFiliaisAsaas}
              disabled={Boolean(acaoEmProcessamento)}
            >
              {acaoEmProcessamento === 'gerar_cobranca' ? 'Ativando...' : 'Ativar todas'}
            </button>
          </div>
        </div>
      )}

      <div className="routine-toolbar" style={{ marginBottom: '14px' }}>
        <div className="routine-title-group">
          <h4 className="routine-section-title">Minha Rede Ativa ({filiaisAtivas.length})</h4>
        </div>
      </div>

      <div className="routine-table-wrap">
        <table className="routine-table">
          <thead>
            <tr>
              <th>Loja</th>
              <th style={{ textAlign: 'center' }}>Status</th>
              <th style={{ textAlign: 'center' }}>Ação</th>
            </tr>
          </thead>
          <tbody>
            {filiaisAtivas.length === 0 ? (
              <tr>
                <td colSpan="3" className="routine-empty">
                  Nenhuma filial ativa encontrada.
                </td>
              </tr>
            ) : (
              filiaisAtivas.map((filial) => {
                const isAgendado = filial.status_vinculo === 'cancelamento_agendado';
                const dataFormatada = filial.data_fim_ciclo
                  ? new Date(`${filial.data_fim_ciclo}T12:00:00`).toLocaleDateString('pt-BR')
                  : '';

                return (
                  <tr key={filial.id}>
                    <td>
                      <div style={{ fontWeight: 700, color: isAgendado ? '#64748b' : '#0f172a' }}>
                        {filial.nome || 'Sem nome'}
                      </div>
                      <div style={{ marginTop: '4px', fontSize: '13px', color: '#64748b' }}>{filial.email}</div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {isAgendado ? (
                        <span className="routine-status routine-status--warning">Corte em {dataFormatada}</span>
                      ) : (
                        <span className="routine-status routine-status--success">Ativo</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {isAgendado ? (
                        <span className="routine-status routine-status--muted">Aguardando corte</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => agendarDesvinculo(filial)}
                          disabled={Boolean(acaoEmProcessamento)}
                          className="routine-btn routine-btn--danger"
                        >
                          {acaoEmProcessamento === 'desvincular' ? 'Agendando...' : 'Desvincular'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AbaMinhasFiliais;
